import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowUpRight, Award, Cake, PartyPopper, ShieldCheck,
  Sparkles, Sprout, TrendingUp, Trophy,
} from "lucide-react";
import api from "../lib/api";
import { LoadingScreen } from "../components/LoadingScreen";

// Map the icon name persisted with each milestone to a lucide component.
// Adding a new milestone with an icon that isn't listed here falls back to
// the generic Award trophy — safe default.
const ICONS = {
  PartyPopper, TrendingUp, Sprout, ShieldCheck, Sparkles, Cake, Award, Trophy,
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long", day: "numeric", year: "numeric",
    });
  } catch { return ""; }
};

export default function Milestones() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unseen, setUnseen] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/milestones");
        if (cancelled) return;
        setItems(data.items || []);
        setUnseen(data.unseen || 0);
        // Immediately mark as seen so the header badge clears on next paint.
        api.post("/milestones/mark-seen").catch(() => {});
      } catch {
        // silent — customer sees the empty state below
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    // Group by year for the timeline scaffolding.
    const out = new Map();
    items.forEach((m) => {
      const year = (m.at || "").slice(0, 4) || "—";
      if (!out.has(year)) out.set(year, []);
      out.get(year).push(m);
    });
    return Array.from(out.entries());
  }, [items]);

  // Bulletproof first paint — no placeholder text flash while fetching.
  if (loading) return <LoadingScreen label="Loading your milestones…" />;

  return (
    <div className="min-h-screen bg-charcoal-50" data-testid="milestones-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-black/5">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-charcoal-900 transition-colors"
            data-testid="milestones-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
          <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">
            Mission Watch
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {/* Hero */}
        <div className="mb-10 sm:mb-14">
          <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400 mb-3">
            Milestones
          </p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-charcoal-900 leading-tight">
            Progress worth celebrating.
          </h1>
          <p className="mt-3 text-charcoal-600 max-w-2xl leading-relaxed">
            Every improvement, every uptime streak, every anniversary — Mission Watch
            notices. This is the record of what your organization has accomplished
            with Mission Watch quietly watching along the way.
          </p>
          {unseen > 0 && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3.5 py-1.5 text-xs font-medium text-emerald-800">
              <Sparkles className="w-3.5 h-3.5" />
              {unseen} new milestone{unseen === 1 ? "" : "s"} to review
            </div>
          )}
        </div>

        {/* Empty state — first-time customer with no scans yet */}
        {items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-black/10 bg-white p-10 sm:p-14 text-center">
            <div className="w-14 h-14 rounded-2xl bg-charcoal-900 grid place-items-center mx-auto mb-5">
              <Trophy className="w-6 h-6 text-emerald-400" />
            </div>
            <h2 className="text-xl font-semibold text-charcoal-900 tracking-tight">
              Your first milestone is right around the corner.
            </h2>
            <p className="mt-3 text-charcoal-500 text-sm leading-relaxed max-w-lg mx-auto">
              Once Mission Watch Protection begins its first monitoring cycle,
              we'll start recognizing improvements, uptime streaks, growth,
              and anniversaries as you reach them.
            </p>
            <Link
              to="/dashboard"
              className="mt-6 inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-charcoal-900 text-white hover:bg-charcoal-800 transition-colors"
              data-testid="milestones-empty-cta"
            >
              Run your first checkup <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Timeline */}
        {items.length > 0 && (
          <div className="relative">
            {/* Vertical spine */}
            <div className="absolute left-[19px] top-2 bottom-2 w-px bg-charcoal-200" aria-hidden />
            <div className="space-y-10">
              {grouped.map(([year, list]) => (
                <section key={year} data-testid={`milestones-year-${year}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-charcoal-50 border border-black/5 grid place-items-center relative z-10">
                      <span className="text-[11px] font-semibold text-charcoal-500">{year.slice(-2)}</span>
                    </div>
                    <div>
                      <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Year</p>
                      <p className="text-base font-semibold text-charcoal-900 tracking-tight">{year}</p>
                    </div>
                  </div>
                  <div className="pl-14 space-y-4">
                    {list.map((m) => (
                      <MilestoneCard key={m.id} milestone={m} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function MilestoneCard({ milestone }) {
  const Icon = ICONS[milestone.icon] || Award;
  return (
    <article
      className="group relative rounded-2xl bg-white border border-black/5 shadow-sm hover:shadow-md hover:border-black/10 transition-all duration-200 p-5 sm:p-6"
      data-testid={`milestone-card-${milestone.key}`}
    >
      {/* Icon dot — sits atop the vertical spine */}
      <div className="absolute -left-[45px] top-6 w-8 h-8 rounded-full bg-white border border-charcoal-200 grid place-items-center shadow-sm">
        <Icon className="w-4 h-4 text-emerald-600" />
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <h3 className="text-base font-semibold text-charcoal-900 tracking-tight" data-testid={`milestone-title-${milestone.key}`}>
          {milestone.title}
        </h3>
        <span className="text-xs font-mono text-charcoal-500">{fmtDate(milestone.at)}</span>
      </div>
      <p className="text-sm text-charcoal-600 leading-relaxed">{milestone.description}</p>
      {/* Optional meta chip — surface concrete numbers for score_improvement etc. */}
      {milestone.meta && Object.keys(milestone.meta).length > 0 && (
        <MetaPills meta={milestone.meta} />
      )}
      {milestone.cta_label && milestone.cta_url && (
        <div className="mt-4">
          <a
            href={milestone.cta_url}
            target={milestone.cta_url.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
            data-testid={`milestone-cta-${milestone.key}`}
          >
            {milestone.cta_label} <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
      )}
    </article>
  );
}

function MetaPills({ meta }) {
  // Render only human-friendly numbers. Skip empty/null values silently.
  const entries = Object.entries(meta || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (entries.length === 0) return null;
  const LABELS = {
    from_score: "Previous score",
    to_score: "New score",
    delta: "Improvement",
    score: "Score",
    pages: "Pages watched",
    days: "Days",
  };
  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {entries.map(([k, v]) => (
        <span
          key={k}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-charcoal-700 bg-charcoal-50 border border-black/5 px-2 py-0.5 rounded-full"
        >
          <span className="text-charcoal-500">{LABELS[k] || k.replace(/_/g, " ")}:</span>
          <span className="font-mono">{k === "delta" ? `+${v}` : String(v)}</span>
        </span>
      ))}
    </div>
  );
}
